import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2, AlertTriangle, MinusCircle } from "lucide-react";
import { fmtDateTime } from "@/lib/date";

const BADGE = {
  updated: ["Updated", "bg-emerald-50 text-emerald-800", CheckCircle2],
  unchanged: ["No changes", "bg-slate-100 text-slate-700", MinusCircle],
  error: ["Failed — previous content kept", "bg-red-50 text-red-800", AlertTriangle],
};

function SourceRow({ s, result }) {
  const [label, cls, Icon] = BADGE[result?.status] || (s.last_error ? BADGE.error : ["—", "bg-slate-50 text-slate-500", MinusCircle]);
  return (
    <li className="py-3 flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4" data-testid={`content-sync-source-${s.slug}`}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-[#0A1F44]">{s.label}</div>
        <div className="text-xs text-slate-500">Last successful check {s.last_success_at ? fmtDateTime(s.last_success_at) : "—"}
          {s.changed_at && <> · content last changed {fmtDateTime(s.changed_at)}</>}</div>
        {(result?.error || s.last_error) && <div className="text-xs text-red-700 mt-1 break-words" data-testid={`content-sync-error-${s.slug}`}>{result?.error || s.last_error}</div>}
      </div>
      <span className={`self-start inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${cls}`}><Icon className="w-3.5 h-3.5" />{label}</span>
    </li>
  );
}

export default function ContentSyncPanel() {
  const [st, setSt] = useState(null);
  const load = useCallback(async () => {
    try { setSt((await api.get("/admin/content-sync")).data); } catch { /* not the owner */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!st?.running) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [st?.running, load]);
  const run = async () => {
    try { await api.post("/admin/content-sync"); toast.success("Sync started"); setSt((s) => ({ ...s, running: true })); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start sync"); load(); }
  };
  if (!st) return null;
  const last = st.last_run;
  const byslug = Object.fromEntries((last?.results || []).map((r) => [r.slug, r]));
  return (
    <Card className="p-4 sm:p-6 space-y-3" data-testid="content-sync-panel">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div>
          <h2 className="font-serif text-xl text-[#0A1F44]">Google Sheets content sync</h2>
          <p className="text-xs text-slate-500 mt-0.5">Checks every {st.interval_minutes} minutes automatically. Only guide content is updated — users, PBOs and progress are never changed.</p>
        </div>
        <Button onClick={run} disabled={st.running} data-testid="content-sync-now" className="rounded-full bg-[#0A1F44] text-white h-11 px-5 shrink-0">
          <RefreshCw className={`w-4 h-4 mr-2 ${st.running ? "animate-spin" : ""}`} />{st.running ? "Syncing…" : "Sync Now"}
        </Button>
      </div>
      <div className="text-xs text-slate-600 flex flex-wrap gap-x-4 gap-y-1" data-testid="content-sync-summary">
        <span>Last full success: <b>{st.last_full_success_at ? fmtDateTime(st.last_full_success_at) : "—"}</b></span>
        {last && <span>Last run: {fmtDateTime(last.started_at)} ({last.trigger === "auto" ? "automatic" : `manual${last.by ? ` by ${last.by}` : ""}`}) · <b data-testid="content-sync-last-status">{last.status}</b></span>}
      </div>
      <ul className="divide-y divide-slate-100">{st.sources.map((s) => <SourceRow key={s.slug} s={s} result={byslug[s.slug]} />)}</ul>
    </Card>
  );
}
